import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

import { formatDeleteErrorMessage } from '../lib/apiError';

type UseDeleteEntityOptions = {
    deleteFn: (id: string) => Promise<unknown>;
    queryKeys: string[];
    successMessageKey: string;
    onDeleted?: () => void;
};

export function useDeleteEntity({
    deleteFn,
    queryKeys,
    successMessageKey,
    onDeleted,
}: UseDeleteEntityOptions) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingId, setPendingId] = useState<string | null>(null);

    const mutation = useMutation({
        mutationFn: deleteFn,
        onSuccess: () => {
            notifications.show({ color: 'green', message: t(successMessageKey) });
            setConfirmOpen(false);
            setPendingId(null);
            onDeleted?.();
            queryKeys.forEach((key) => {
                void queryClient.invalidateQueries({ queryKey: [key] });
            });
        },
        onError: (error: unknown) => {
            notifications.show({
                color: 'red',
                title: t('common.delete.failedTitle'),
                message: formatDeleteErrorMessage(error, t),
                autoClose: false,
            });
        },
    });

    const requestDelete = (id: string) => {
        setPendingId(id);
        setConfirmOpen(true);
    };

    const closeConfirm = () => {
        if (!mutation.isPending) {
            setConfirmOpen(false);
            setPendingId(null);
        }
    };

    const confirmDelete = () => {
        if (pendingId) {
            mutation.mutate(pendingId);
        }
    };

    return {
        requestDelete,
        confirmDelete,
        closeConfirm,
        confirmOpen,
        isPending: mutation.isPending,
    };
}
